import {Injectable} from '@angular/core';
import {CollectionAccessType, CollectionOrderDir, CollectionType} from "./collection-utils";
import {map, Observable} from "rxjs";
import {
    Collection, CollectionAccessGrant,
    CollectionAccessUpdate,
    CollectionData,
    CollectionShareLinkData,
    CollectionUpdate, PublishedCollection, ReadonlyPermission, SharedCollection
} from "./collection.model";
import {HttpClient} from '@angular/common/http';
import {ApiResponse, getDataOrFail, getPageOrFail, Page, PagedResponse} from "../common/api-utils";
import {OrderBy} from "../assets/order-by";

const DEFAULT_PAGE_SIZE = 100;

@Injectable({
    providedIn: 'root'
})
export class CollectionService {

    constructor(
        private http: HttpClient
    ) {
    }

    getCollections(
        type?: CollectionType,
        access?: CollectionAccessType,
        searchTerm?: string,
        orderBy?: OrderBy,
        orderDir?: CollectionOrderDir,
        page?: number,
        pageSize?: number
    ): Observable<Page<Collection>> {
        return this.http.get<PagedResponse<Collection>>('/api/collections.json', {
            params: {
                access: access || '',
                page: page || 1,
                resultsPerPage: pageSize || DEFAULT_PAGE_SIZE,
                type: type || '',
                q: searchTerm || '',
                orderBy: orderBy || 'createdAt',
                order: orderDir || 'asc'
            }
        }).pipe(map(getPageOrFail));
    }

    getCollection(id: string): Observable<Collection> {
        return this.http.get<ApiResponse<Collection>>(`/api/collection/${id}.json`).pipe(map(getDataOrFail));
    }

    createCollection(data: CollectionData): Observable<Collection> {
        return this.http.post<ApiResponse<Collection>>('/api/collection.json', data).pipe(map(getDataOrFail));
    }

    updateCollection(id: string, collection: CollectionUpdate): Observable<Collection> {
        return this.http.put<ApiResponse<any>>(`/api/collection/${id}.json`, collection).pipe(map(getDataOrFail));
    }

    deleteCollection(id: string): Observable<any> {
        return this.http.delete<ApiResponse<any>>(`/api/collection/${id}.json`).pipe(map(getDataOrFail));
    }

    addAssetsToCollection(collectionId: string, assetIds: string[]): Observable<any> {
        return this.http.post<ApiResponse<any>>(`/api/collection/${collectionId}/addAssets.json`, assetIds).pipe(map(getDataOrFail));
    }

    removeAssetFromCollection(collectionId: string, assetIds: string[]): Observable<any> {
        return this.http.post<ApiResponse<any>>(`/api/collection/${collectionId}/removeAssets.json`, assetIds).pipe(map(getDataOrFail));
    }

    getPublishedCollection(slug: string): Observable<PublishedCollection> {
        return this.http.get<ApiResponse<PublishedCollection>>(`/api/collection/public/${slug}.json`).pipe(map(getDataOrFail));
    }

    publishCollection(id: string, permission: ReadonlyPermission): Observable<Collection> {
        return this.http.put<ApiResponse<Collection>>(`/api/collection/${id}/publication.json`, {permission}).pipe(map(getDataOrFail));
    }

    removeCollectionPublication(id: string): Observable<Collection> {
        return this.http.delete<ApiResponse<any>>(`/api/collection/${id}/publication.json`).pipe(map(getDataOrFail));
    }

    getSharedCollection(key: string): Observable<SharedCollection> {
        return this.http.get<ApiResponse<SharedCollection>>(`/api/collection/shared/${key}.json`).pipe(map(getDataOrFail));
    }

    shareCollection(id: string, data: CollectionShareLinkData): Observable<Collection> {
       return this.http.post<ApiResponse<Collection>>(`/api/collection/${id}/sharedLink.json`, data).pipe(map(getDataOrFail));
    }

    updateCollectionShare(id: string, data: CollectionShareLinkData): Observable<Collection> {
        return this.http.put<ApiResponse<Collection>>(`/api/collection/${id}/sharedLink.json`, data).pipe(map(getDataOrFail));
    }

    revokeCollectionShare(id: string): Observable<Collection> {
        return this.http.delete<ApiResponse<any>>(`/api/collection/${id}/sharedLink.json`).pipe(map(getDataOrFail));
    }

    addUserAccessToCollection(id: string, data: CollectionAccessGrant): Observable<Collection> {
        return this.http.post<ApiResponse<Collection>>(`/api/collection/${id}/access.json`, data).pipe(map(getDataOrFail));
    }

    updateUserAccessToCollection(id: string, data: CollectionAccessUpdate): Observable<Collection> {
        return this.http.put<ApiResponse<Collection>>(`/api/collection/${id}/access/${data.accessId}.json`, data).pipe(map(getDataOrFail));
    }

    removeUserAccessFromCollection(id: string, accessId: number): Observable<any> {
        return this.http.delete<ApiResponse<any>>(`/api/collection/${id}/access/${accessId}.json`).pipe(map(getDataOrFail));
    }

    transferOwnership(collectionIds: string[], userId: number): Observable<Collection> {
        return this.http.post<ApiResponse<any>>(`/api/collection/transferOwnership.json`, {
            collections: collectionIds,
            user: userId
        }).pipe(map(getDataOrFail));
    }
}
